import React, { FC, useCallback, useMemo, useState } from "react";
import {
  faAdd,
  faCalendarPen,
  faPenToSquare,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import ButtonAdd from "../atoms/ButtonAdd";
import { NutritionProgress } from "@/app/nutrition/plan";
import { NutritionProgressListItem } from "../molecules/NutritionProgressListItem";
import { useTranslation } from "react-i18next";
import { ButtonSecondary } from "../atoms/ButtonSecondary";

export type NutritionProgressListProps = {
  isLoading?: boolean;
  progress: NutritionProgress[];
  onCreateWithAlgorithm: () => void;
  onCreateFromScratch: () => void;
  onUpdateActive: () => void;
  onUpdatePlanned: () => void;
  onUpdateCompleted: () => void;
  onSearch: () => void;
  onView: (progress: NutritionProgress) => void;
  onViewFile: () => void;
};

export const NutritionProgressList: FC<NutritionProgressListProps> = ({
  isLoading,
  onCreateWithAlgorithm,
  onCreateFromScratch,
  onUpdateActive,
  onUpdatePlanned,
  onUpdateCompleted,
  onSearch,
  onView,
  onViewFile,
  progress,
}) => {
  const { t } = useTranslation();
  const [optionsVisible, setOptionsVisible] = useState(false);

  const handleToggleCreate = useCallback(() => {
    setOptionsVisible((prev) => !prev);
  }, []);

  const completedNutritionProgress = useMemo(() => {
    return progress.find((item) => item.status === 0);
  }, [progress]);

  const activeNutritionProgress = useMemo(() => {
    return progress.find((item) => item.status === 1);
  }, [progress]);

  const plannedNutritionProgress = useMemo(() => {
    return progress.find((item) => item.status === 3);
  }, [progress]);

  if (isLoading) {
    return (
      <div className="w-full mt-4 animate-pulse">
        <div className="h-6 bg-gray-300 rounded w-1/4 mb-4"></div>
        <div className="h-10 bg-gray-200 rounded w-full mb-4"></div>
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="flex items-center justify-between py-5 border-b"
          >
            <div className="w-3/4 h-4 bg-gray-200 rounded"></div>
            <div className="w-1/6 h-4 bg-gray-300 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="w-full mt-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 px-2">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {t("contexts.nutrition.nutrition_plans")}
          </h1>
          <div className="flex gap-4 lg:gap-0 mt-4 lg:mt-0 lg:flex-row lg:space-x-4">
            <ButtonSecondary
              text={t("user.views.nutrition_file")}
              size="medium"
              onClick={onViewFile}
            />
            <Menu as="div" className="relative flex-none mt-1 md:mt-0">
              <MenuButton className="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900">
                <span className="sr-only">Open options</span>
                <ButtonAdd functionButton={() => {}} />
              </MenuButton>
              <MenuItems
                transition
                className="absolute md:right-0 z-50 mt-2 w-60 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
              >
                <div>
                  <div
                    onClick={handleToggleCreate}
                    className="flex items-center px-3 py-1 text-sm leading-6 text-gray-900 data-[focus]:bg-gray-50 text-left gap-4 cursor-pointer"
                  >
                    <FontAwesomeIcon icon={faAdd} />
                    Nuevo plan
                  </div>
                </div>
                {optionsVisible && (
                  <div className="pl-4">
                    <MenuItem>
                      <a
                        onClick={onCreateWithAlgorithm}
                        className="flex items-center px-3 py-1 text-sm leading-6 text-gray-900 data-[focus]:bg-gray-50 text-left gap-4 cursor-pointer"
                      >
                        Nuevo plan del algoritmo
                      </a>
                    </MenuItem>
                    <MenuItem>
                      <a
                        onClick={onCreateFromScratch}
                        className="flex items-center px-3 py-1 text-sm leading-6 text-gray-900 data-[focus]:bg-gray-50 text-left gap-4 cursor-pointer"
                      >
                        Nuevo plan desde cero
                      </a>
                    </MenuItem>
                  </div>
                )}
                <MenuItem disabled={!plannedNutritionProgress}>
                  <a
                    className={`${plannedNutritionProgress ? "text-gray-900 cursor-pointer" : "text-gray-300 cursor-not-allowed"} flex items-center px-3 py-1 text-sm leading-6  data-[focus]:bg-gray-50 text-left gap-4 `}
                    onClick={onUpdatePlanned}
                  >
                    <FontAwesomeIcon icon={faCalendarPen} />
                    Modificar plan planeado
                  </a>
                </MenuItem>
                {activeNutritionProgress ? (
                  <MenuItem disabled={!activeNutritionProgress}>
                    <a
                      className={`${activeNutritionProgress ? "text-gray-900 cursor-pointer" : "text-gray-300 cursor-not-allowed"} flex items-center px-3 py-1 text-sm leading-6  data-[focus]:bg-gray-50 text-left gap-4 `}
                      onClick={onUpdateActive}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                      Modificar plan activo
                    </a>
                  </MenuItem>
                ) : (
                  <MenuItem disabled={!completedNutritionProgress}>
                    <a
                      className={`${completedNutritionProgress ? "text-gray-900 cursor-pointer" : "text-gray-300 cursor-not-allowed"} flex items-center px-3 py-1 text-sm leading-6  data-[focus]:bg-gray-50 text-left gap-4 `}
                      onClick={onUpdateCompleted}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                      Modificar último completado
                    </a>
                  </MenuItem>
                )}
                <MenuItem>
                  <a
                    className="flex items-center px-3 py-1 text-sm leading-6 text-gray-900 data-[focus]:bg-gray-50 text-left gap-4 cursor-pointer"
                    onClick={onSearch}
                  >
                    <FontAwesomeIcon icon={faSearch} />
                    Buscar plan
                  </a>
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>
        </div>
        <hr className="border-gray-300 mb-4" />
        <ul
          role="list"
          className="divide-y divide-gray-100 px-2 h-96 overflow-auto"
        >
          {progress?.map((progress) => (
            <NutritionProgressListItem
              progress={progress}
              onClick={() => onView(progress)}
              onView={() => onView(progress)}
            />
          ))}
        </ul>
      </div>
    </>
  );
};
